import { action, observable } from 'mobx';
import stores from '../../../models/Stores';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import { MapView, FeatureSet } from 'cd-eft/utils/esriTypes';
import Layer from '@arcgis/core/layers/Layer';
import { resetMap } from 'cd-eft/components/SurveyEngineering/Maps/SETMapFunctions/mapOperations';
import { DesignContainerVM } from 'cd-eft/components/Design/DesignContainer/DesignContainerVM';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import * as SETAPI from 'cd-eft/services/eftSetApi';
import { DesignElementResponse } from 'cd-eft/_codegen_/openapi-generator/eft-set';
import { AxiosResponse } from 'axios';
import { appConfig } from 'cd-eft/config/AppConfig';
import { EngineeringMapService } from 'cd-eft/framework/mapServices/EngineeringMapServices';
import { INotification } from 'cd-eft/models/Stores/DesignStore';

const { landLevelingDesignStore, designStore, projectsLibraryStore } = stores.AppStore;
const setStore = stores.AppStore.surveyEngineeringStore;
const gisModel = stores.AppStore.surveyEngineeringStore.gisModel;

export class LandLevelingSummaryVM {
  @observable props;
  @observable isLoading: boolean = false;
  @observable jobExecuting: boolean = false;
  @observable jobMessage: string = '';
  @observable mapServiceLayer: any = MapImageLayer;
  @observable output_ground_points: any = FeatureSet;
  @observable notification: INotification = {
    message: '',
    header: '',
    type: '',
    hidden: true,
    timestamp: undefined,
  };
  @observable error = false;
  @observable errorMsg = '';
  @observable selectDisplayLandLevelings = false;
  @observable selectSurface = false;
  @observable displayType = 'none';
  @observable someLandLevelingSelected = false;
  @observable selectedLandLevelingsInfo: any = [];
  @observable landLevelingLayers: Layer[] = [];

  private mapView?: MapView;
  constructor(props: any) {
    this.props = props;
    if (props.mapView) {
      this.mapView = props.mapView;
    }
  }

  @observable elementActions = {
    Edit: { action: this.openSingleLandLeveling },
    Delete: { action: this.deleteDesignElement },
  };

  @action.bound clearNotification(): void {
    this.notification = { message: '', header: '', type: '', hidden: true, timestamp: undefined };
  }

  @action.bound statusCallback(jobInfo: __esri.JobInfo) {
    let messageIndex = 0;
    if (jobInfo && jobInfo.messages && jobInfo.messages.length > 0) {
      if (jobInfo.messages) {
        if (jobInfo.messages.length > messageIndex)
          for (let i = messageIndex; i < jobInfo.messages.length; i++) {
            this.jobMessage = jobInfo.messages[i].description;
          }
        messageIndex = jobInfo.messages.length;
      }
    }
  }

  @action.bound addDesign() {
    landLevelingDesignStore.showLoader = true;
    landLevelingDesignStore.currentTab = 'New Land Leveling';
    landLevelingDesignStore.hasDesign = true;
    designStore.onNewStructureTab = true;
  }

  @action.bound async onClickSurface() {
    resetMap();
    designStore.hideCrossSectionToolbar = true;
    designStore.hideSingleCrossSection = true;
    landLevelingDesignStore.showLoader = true;

    landLevelingDesignStore.displayedLandLevelingsIds = [];
    this.selectedLandLevelingsInfo.forEach((item) => {
      landLevelingDesignStore.displayedLandLevelingsIds.push(item.designElementId)
    });

    const input_structure_json_ids: number[] = landLevelingDesignStore.displayedLandLevelingsIds.map((item) => {
      return item as number;
    });
    let i = 0;
    while (i < landLevelingDesignStore.landLevelingLayers.length) {
      const layer = landLevelingDesignStore.landLevelingLayers[i];
      const status = input_structure_json_ids.findIndex((id) => id.toString() === layer.id);
      if (status === -1) {
        landLevelingDesignStore.landLevelingLayers.splice(status, 1);
      } else {
        i++;
      }
    }
    try {
      if (0 < input_structure_json_ids.length) {
        await DesignContainerVM.loadMultiStructureSurface(input_structure_json_ids);
      } else {
        await DesignContainerVM.displayOriginalSurface();
      }
      landLevelingDesignStore.latestElementId = undefined;
      setStore.pointsOnMap = false;
      this.mapView!.ui.add(gisModel.locationWidget, 'bottom-left');
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    } finally {
      setTimeout(() => {
        setStore.isLoading = false;
      }, 1000);
    }
    landLevelingDesignStore.showLoader = false;
  }

  @action.bound initLandLeveling() {
    landLevelingDesignStore.showLoader = true;
    designStore.designElementTypeId = appConfig.hardCodes.designElementTypeLandLeveling //To enable ToolBar on the map for Landlevelling
    // need to check for surfaceDefID right when the component loads, since you cannot use land leveling without one
    // check for project with session and surface
    if (
      designStore.currentSurface &&
      designStore.currentSurface.surfaceDefId &&
      designStore.projectsStore.currentSessionId
    ) {
      this.getProjectDesignElements();
    } else {
      landLevelingDesignStore.showLoader = false; //Surface NOT found.
    }
  }

  @action.bound async getProjectDesignElements() {
    landLevelingDesignStore.landLevelingSummaryTable = [];
    this.selectedLandLevelingsInfo = [];
    landLevelingDesignStore.showLoader = true;
    designStore.designElementId = 0;

    const structureList = 'Land Leveling';
    try {
      const response = await SETAPI.DesignElementController.getAllDesignElementsBySessionAndTypeUsingGET(
        designStore.projectsStore.currentSessionId,
        structureList
      );
      let data = response['data'] as any;
      if (data) {
        landLevelingDesignStore.landLevelingSummaryTable = [];

        //land leveling structures
        data = data.filter(e => e.designElementTypeId === appConfig.hardCodes.designElementTypeLandLeveling)
        data.map((item) => this.mapIdToName(item));
        data.map((item, index) => {
          item.checked = false;
        });

        landLevelingDesignStore.landLevelingSummaryTable.push(...data);

        // data.forEach((item, index) => {
        //   const selected = landLevelingDesignStore.selectedLandLevelingsInfo.find((landLeveling) => {
        //     return landLeveling.designElementId === item.designElementId;
        //   });
        //   if (selected) {
        //     const e = {
        //       target: {
        //         checked: true,
        //       },
        //     };
        //     this.handleCheckboxChange(e, index);
        //   }
        // });
        data.map((item, index) => {
          item.checked = false;
          if (landLevelingDesignStore.landLevelingLayers.some((layer) => layer.id === `${item.designElementId}`)) {
            const e = {
              target: {
                checked: true,
              },
            };
            this.handleCheckboxChange(e, index);
          }
        });

        landLevelingDesignStore.showLoader = false;
      }
    } catch (err) {
      landLevelingDesignStore.showLoader = false;
      console.error(err);
      this.error = true;
      this.errorMsg = `Uh oh - something went wrong. ${err}`;
    }
  }

  @action.bound mapIdToName(item) {
    switch (item.designElementTypeId) {
      case 15:
        item.designElementTypeId = {
          name: 'Land Leveling',
          id: 15,
        };
        break;
      default:
        return;
    }
  }

  @action.bound parseAndConvert(data: DesignElementResponse): void {
    const parsedData = JSON.parse(data.json!);
    landLevelingDesignStore.newDesignForm = parsedData;
    landLevelingDesignStore.newLandLevelingResults = parsedData;
    landLevelingDesignStore.newDesignForm.designElementId = data.designElementId!;
  }

  @action.bound async openSingleLandLeveling(event, item) {
    const id: string = item.designElementId.toString();
    try {
      const response: AxiosResponse<
        DesignElementResponse[]
      > = await SETAPI.DesignElementController.surveyDesignElementByIdUsingGET(id);
      const data: DesignElementResponse[] = response['data'];

      if (data[0] && this.mapView) {
        this.parseAndConvert(data[0]);
        landLevelingDesignStore.pendingEdits = { name: false, design: false, unsavedEdits: true };
        landLevelingDesignStore.hasDesign = true;
        landLevelingDesignStore.editDesign = true;
        landLevelingDesignStore.showLoader = true;
        landLevelingDesignStore.currentTab = 'New Land Leveling';
        const index = landLevelingDesignStore.landLevelingSummaryTable.findIndex((landLeveling) => {
          return landLeveling.designElementId === Number(id)
        });
        if (index !== -1) {
          landLevelingDesignStore.landLevelingSummaryTable[index].checked = true;
          this.selectedLandLevelingsInfo.push(landLevelingDesignStore.landLevelingSummaryTable[index]);
        }
        const layerIndex = landLevelingDesignStore.landLevelingLayers.findIndex((layer) => layer.id === id);
        if (layerIndex !== -1) {
          this.mapView.map.remove(landLevelingDesignStore.landLevelingLayers[layerIndex]);
          landLevelingDesignStore.landLevelingLayers.splice(layerIndex, 1);
        }
        landLevelingDesignStore.originalLandLevelingResults = landLevelingDesignStore.newLandLevelingResults;
        landLevelingDesignStore.displayLandLevelingGraphic(
          this.mapView,
          landLevelingDesignStore.newDesignForm,
          true,
          projectsLibraryStore.projectSpatialReference!,
          id
        );
        landLevelingDesignStore.originalDesignLayer = landLevelingDesignStore.currentDesignLayer;
        landLevelingDesignStore.originalIdString = id;
        designStore.structureLayerExists = true;
      }
    } catch (err) {
      console.error(err);
    }
  }

  @action.bound handleCheckboxChange(e: any, index, updateArray?: boolean) {
    landLevelingDesignStore.landLevelingSummaryTable[index].checked = e.target.checked;
    if (e.target.checked) {
      this.selectedLandLevelingsInfo.push(landLevelingDesignStore.landLevelingSummaryTable[index]);
    } else {
      this.selectedLandLevelingsInfo.replace(
        this.selectedLandLevelingsInfo.filter((p, j) => {
          return !(p === landLevelingDesignStore.landLevelingSummaryTable[index]);
        })
      );
    }
    this.someLandLevelingSelected = this.selectedLandLevelingsInfo.length > 0 ? true : false;
    // if (updateArray) landLevelingDesignStore.selectedLandLevelingsInfo = this.selectedLandLevelingsInfo;
  }

  @action.bound async displaySelectedLandLeveling() {
    const view = this.mapView;
    if (view) landLevelingDesignStore.removeAllLandLevelingLayers(view);
    const ids = this.selectedLandLevelingsInfo.map((a) => a.designElementId);
    if (ids.length > 0) {
      const response: AxiosResponse<
        DesignElementResponse[]
      > = await SETAPI.DesignElementController.surveyDesignElementByIdUsingGET(ids);

      for (const designElement of response['data']) {
        if (designElement?.json) {
          if (designElement.designElementTypeId === 15) {
            const landLeveling: any = JSON.parse(designElement?.json);
            if (view)
              landLevelingDesignStore.displayLandLevelingGraphic(
                view,
                landLeveling,
                false,
                projectsLibraryStore.projectSpatialReference!,
                designElement.designElementId!.toString()
              );
          } else {
            console.error('applyStructuresToMap:pads', 'Unrecognized/unsupported designElementTypeId.');
          }
        }
      }
    }
    if (view && !designStore.hideSingleCrossSection) await designStore.validateSingleCrossSection(view);
  }

  @action.bound async deleteDesignElement(event, item) {
    landLevelingDesignStore.showLoader = true;
    const designElementId = item.designElementId;
    try {
      await SETAPI.DesignElementController.deleteDesignElementUsingPOST(designElementId);
      if (designElementId === landLevelingDesignStore.latestElementId) {
        await DesignContainerVM.displayOriginalSurface();
        landLevelingDesignStore.latestElementId = undefined;
      }
      const layerIndex = landLevelingDesignStore.landLevelingLayers.findIndex(
        (layer) => layer.id === designElementId.toString()
      );
      if (layerIndex !== -1) {
        if (this.mapView) {
          this.mapView.map.remove(landLevelingDesignStore.landLevelingLayers[layerIndex]);
        }
        landLevelingDesignStore.landLevelingLayers.splice(layerIndex, 1);
      }
      let map = EngineeringMapService.instance?.mapView?.map;
      designStore.removeDesignedContours(map);
      this.getProjectDesignElements();

      // prepare displayed land levelings
      const index = landLevelingDesignStore.displayedLandLevelingsIds.indexOf(designElementId);
      if (index >= 0) {
        landLevelingDesignStore.displayedLandLevelingsIds.splice(index, 1);
      }
      const rest_structure_json_ids: number[] = landLevelingDesignStore.displayedLandLevelingsIds.map((id) => {
        return id;
      });
      if (0 < rest_structure_json_ids.length) {
        await DesignContainerVM.loadMultiStructureSurface(rest_structure_json_ids);
      }
      if (landLevelingDesignStore.landLevelingSummaryTable.length == 0) {
        await DesignContainerVM.displayOriginalSurface();
        landLevelingDesignStore.latestElementId = undefined;
        landLevelingDesignStore.displayedLandLevelingsIds = [];
      }
    } catch (e) {
      console.warn('deleteDesignElement error = ', e);
    } finally {
      if (this.mapView && !designStore.hideSingleCrossSection) await designStore.validateSingleCrossSection(this.mapView);
    }
  }
}

